import { Ban, CheckCircle2, Layers, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { GoalSectorModal } from '../components/GoalSectorModal';
import { goalSectorsApi } from '../budgetGoalsApi';
import type { GoalSectorListItem } from '../budgetGoalTypes';

type TabKey = 'info';

/**
 * One goal sector.
 *
 * On the **header** layout: a catalog row reads as a single continuous thing — a name, what
 * the forms do with it, and the trail of who changed it — not a record whose content splits
 * into panels a reader chooses between.
 */
export function GoalSectorProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [sector, setSector] = useState<GoalSectorListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      goalSectorsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setSector(next);

          // Cleared on success, or a failed restore would keep its banner over a page that has
          // since re-read the record and disagrees with it.
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this sector.', 0));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /** No confirm tick and no password: restoring puts something back. */
  async function restore() {
    if (!id) return;

    setRestoring(true);
    setError(null);

    try {
      await goalSectorsApi.activate(id);
      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('Could not restore this sector.', 0),
      );
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Investment Planning',
    { label: 'Settings', to: '/procurement/investment-planning/settings' },
    { label: 'Sectors', to: '/procurement/investment-planning/settings/goal-sectors' },
    sector?.name ?? 'Sector',
  ];

  // Guarded on having NOTHING to show, never on `error` alone: once a record is up it is still
  // the last good answer, and a failed restore goes to the banner rather than blanking the page.
  if (error && !sector) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (loading || !sector) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const { subSectorCount, goalCount } = sector;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="goal-sector-profile"
        header={
          <ProfileHeader
            id="goal-sector-profile-header"
            icon={<Layers className="h-6 w-6" aria-hidden="true" />}
            title={sector.name}
            meta={[
              `${subSectorCount} ${subSectorCount === 1 ? 'sub-sector' : 'sub-sectors'}`,
              `${goalCount} ${goalCount === 1 ? 'goal' : 'goals'}`,
            ]}
            status={
              <StatusText tone={sector.isActive ? 'active' : 'inactive'}>
                {sector.isActive ? 'Active' : 'Deleted'}
              </StatusText>
            }
            createdBy={sector.createdByName}
            createdAt={sector.createdAt}
          />
        }
        banner={
          error ? (
            <ProfileBanner
              id="goal-sector-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message={error.message}
            />
          ) : sector.isActive ? (
            <ProfileBanner
              id="goal-sector-profile-banner"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This sector is offered: it appears in the Sector picker when a sub-sector is filed, and the goals beneath it are offered to a budget plan's Goal Setting."
            />
          ) : (
            <ProfileBanner
              id="goal-sector-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This sector is deleted. Its sub-sectors and goals are kept, but they are no longer offered to a budget plan — and nothing new can be filed here."
              action={
                // Gates the button, not the banner: a read-only viewer still needs the state.
                <PermissionGate permission="budget-goals.delete">
                  <Button
                    id="goal-sector-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => void restore()}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="goal-sector-details"
          title="Details"
          menuItems={[
            {
              key: 'edit',
              label: 'Edit',
              permission: 'budget-goals.update',
              onClick: () => setEditing(true),
            },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: sector.name },
              { label: 'Description', value: sector.description },
              {
                // The STORED order, labelled as such. It is an identifier rather than a
                // quantity, so it is mono and NOT muted at zero.
                label: 'Order',
                value: <span className="font-mono tabular-nums">{sector.sortOrder}</span>,
              },
              {
                label: 'Sub-sectors',
                /*
                  A zero is MUTED, like every other quantity: a sector nobody has filed under is
                  the ordinary case. Muted, never an em dash — "none" and "we do not know" are
                  different facts.
                */
                value: (
                  <span className={`font-mono tabular-nums ${subSectorCount === 0 ? 'text-slate-300' : ''}`}>
                    {subSectorCount}
                  </span>
                ),
              },
              {
                label: 'Goals beneath it',
                value: (
                  <span className={`font-mono tabular-nums ${goalCount === 0 ? 'text-slate-300' : ''}`}>
                    {goalCount}
                  </span>
                ),
              },
            ]}
          />
        </ProfileCard>

        {/* Audit is never editable: created and updated are a record of what happened. */}
        <ProfileCard id="goal-sector-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: sector.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(sector.createdAt)} className="font-mono">
                    {formatDateTime(sector.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: sector.updatedByName },
              {
                label: 'Updated',
                value: sector.updatedAt ? (
                  <time dateTime={toIso(sector.updatedAt)} className="font-mono">
                    {formatDateTime(sector.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <GoalSectorModal
          open
          editing={sector}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
